import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useCourse from '@/composables/useCourse'
  import { CourseModule } from '@/store/modules/course'
  import useIcons from '@/composables/useIcons'
  import NonFreeLesson from '@/components/course/NonFreeLesson.vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import CourseApi from '@/services/api/CourseApi'
  import QuestionWithButtons from '@/components/common/QuestionWithButtons.vue'
  import useSegment from '@/composables/useSegment'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useGamification from '@/composables/useGamification'
  import GuidedQuizResults from '@/components/course/GuidedQuizResults.vue'
  import BarChart from '@/components/common/BarChart.vue'
  import { useRouter } from 'vue-router/composables'
  import useI18n from '@/composables/useI18n'
  import type { GuidedQuizCompletionAbstractResult, Question, QuestionAnswer } from '@/services/interfaces/QuizQuestion'
  import UsersApi from '@/services/api/UsersApi'
  import { UserModule } from '@/store/modules/user'
  import CookieHelper from '@/helpers/CookieHelper'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'GuidedQuiz',
  props: {
    isEmbedded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['show-guided-quiz-register-modal', 'embedded-quiz-loaded'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const router = useRouter()
  const { currentCourseId, moduleId, course, studyingStatus, nextCourseModule, nextLesson } = useCourse()
  const { trackPage, pageViewed, guidedQuizCompleted } = useSegment()
  const { trackGamificationActivity } = useGamification()
  const {
    convertNumberAnswer,
    shuffleArray,
    formatDate,
    getColorVariableValue,
    correctNumberAnswer,
    fromEmbeddedQuiz,
    scrollToTop,
  } = useCommonMixin()
  const { translateString } = useI18n()
  const { arrowRight } = useIcons()
  const { isMobileDevice, isDesktopDevice } = useBreakpoint()

  const fetchingQuizCompletion = ref(true)
  const fetchingEmbeddedQuizCompletion = ref(true)
  const questionIndex = ref(0)
  const wrongQuestions = ref([] as Question[])
  const selectedAnswers = ref([] as QuestionAnswer[])
  const startedQuiz = ref(false)
  const calculatedPoints = ref(false)
  const completedGuidedQuiz = ref(false)
  const postingQuizAnswer = ref(false)
  const redoingWrongAnswers = ref(false)
  const guidedQuizCompletions = ref([] as GuidedQuizCompletionAbstractResult[])

  const loadingGuidedQuiz = computed(() => fetchingQuizCompletion.value)

  const currentModule = computed(() => CourseModule.currentModule(currentCourseId.value, moduleId.value))

  const guidedQuiz = computed(() => currentModule.value?.guidedQuiz)

  const currentGuidedQuizCompletionId = computed(() => guidedQuiz.value?.latestGuidedQuizCompletionId ?? null)

  const actualQuestions = computed(() => latestCompletionQuestions.value)

  const currentQuestion = computed(() => {
    if (redoingWrongAnswers.value) return wrongQuestions.value[questionIndex.value]
    return actualQuestions.value[questionIndex.value]
  })

  const currentCorrectAnswers = computed(() => currentQuestion.value?.answers.filter((ans) => ans.isCorrect))

  const points = reactive({
    correct: 0,
    wrong: 0,
  })

  const questionAnswered = computed(() => {
    return selectedAnswers.value.some((ans) => ans.questionId === currentQuestion.value?.questionId)
  })

  const isLastQuestion = computed(() => actualQuestions.value.length === questionIndex.value + 1)

  const getWrongQuestions = computed(() => {
    return actualQuestions.value.filter((question) => {
      const answeredQuestion = selectedAnswers.value.find((q) => q.questionId === question.questionId)
      const correctAnswers = question.answers.filter((a) => a.isCorrect)
      return !(
        answeredQuestion?.selectedAnswerIds.length === correctAnswers.length &&
        correctAnswers.every((ans) => {
          if (question.questionType === 'number') {
            return convertNumberAnswer(answeredQuestion.numberAnswer!) === ans.answer
          }
          return answeredQuestion.selectedAnswerIds.includes(ans.answerId)
        })
      )
    })
  })

  const totalQuestions = computed(() => {
    if (redoingWrongAnswers.value) return wrongQuestions.value?.length
    return actualQuestions.value.length
  })

  const hasNewQuestions = computed(() => {
    if (!guidedQuiz.value?.numberOfQuestions) return false
    return guidedQuiz.value.numberOfQuestions > totalQuestions.value
  })

  /*
   * BE randomizes questions from the module i.e. guidedQuiz.questions.
   * However, the questions it returns from a completion aren't randomized
   * So, what we want to do is to :
   *     1. Use questions from the module ONLY when the latestGuidedQuizCompletionId on the module is null
   *     2. Use questions from a completion when there's a valid latestGuidedQuizCompletionId
   */
  const guidedQuizCompletion = computed(() => CourseModule.guidedQuizCompletions[moduleId.value])
  const latestCompletionQuestions = computed(() => guidedQuizCompletion.value?.questions || [])

  const currentQuestionIndexInCompletion = computed(() => {
    return (questionId: string) => {
      return guidedQuizCompletion.value?.questions.findIndex((q) => q.questionId === questionId)
    }
  })

  const isAPurchasedCourse = computed(() => CourseModule.course?.purchased)

  const answeredQuestionCorrectly = computed(() => {
    return (selectedAnswerIds: string[], correctAnswers: string[]) => {
      return (
        selectedAnswerIds.every((answerId) => correctAnswers.includes(answerId)) &&
        selectedAnswerIds.length === correctAnswers.length
      )
    }
  })

  const completionsDateChartLabels = computed(() => {
    const chartLabels = guidedQuizCompletions.value.map((c) => formatDate(c.performedDate, 'dd LLL yy')).reverse()
    if (chartLabels.length < 5) {
      const daysToPrefill = 5 - chartLabels.length
      for (let i = 0; i < daysToPrefill; i++) {
        chartLabels.unshift('')
      }
    }
    return chartLabels
  })

  const completionsPassedPercentageChartData = computed(() => {
    if (guidedQuizCompletions.value.length === 0) return []
    const passedPercentage: number[] = []
    guidedQuizCompletions.value.forEach((completion) => {
      let totalAnsweredQuestions = 0
      let correctQuestions = 0

      completion.questions.forEach((q) => {
        let userAnswers = q.answers.filter((ans) => ans.selected)
        if (q.questionType === 'number' && q.userNumberAnswer) {
          totalAnsweredQuestions++
          userAnswers = [{ answerId: q.answers[0].answerId, answer: q.userNumberAnswer }]
        } else if (q.questionType === 'single' || q.questionType === 'multiple') {
          if (q.answers.some((a) => a.selected)) {
            totalAnsweredQuestions++
          }
        }

        const correctAnswers = q.answers.filter((a) => a.isCorrect)
        const isCorrectNumberQuestion =
          q.questionType === 'number' ? correctNumberAnswer(q.userNumberAnswer ?? '', q.answers[0].answer) : true
        if (
          answeredQuestionCorrectly.value(
            userAnswers.map((a) => a.answerId),
            correctAnswers.map((a) => a.answerId),
          ) &&
          isCorrectNumberQuestion
        ) {
          correctQuestions++
        }
      })
      passedPercentage.push(Math.round((correctQuestions / totalAnsweredQuestions) * 100))
    })
    if (passedPercentage.length < 5) {
      const daysToPrefill = 5 - passedPercentage.length
      for (let i = 0; i < daysToPrefill; i++) {
        passedPercentage.push(0)
      }
    }
    return passedPercentage.reverse()
  })

  const chartBackgroundColors = computed(() => {
    const colors: string[] = new Array(completionsPassedPercentageChartData.value.length)
    colors.fill(
      getColorVariableValue.value('--secondary-color'),
      0,
      completionsPassedPercentageChartData.value.length - 1,
    )
    colors.fill(getColorVariableValue.value('--primary-color'), completionsPassedPercentageChartData.value.length - 1)
    return colors
  })

  const completionsChartData = computed(() => {
    return {
      labels: completionsDateChartLabels.value,
      datasets: [
        {
          label: translateString('courses.guidedQuizCompletionsChart.quizPassedPercentage'),
          backgroundColor: chartBackgroundColors.value,
          data: completionsPassedPercentageChartData.value,
        },
      ],
    }
  })

  const chartLabels = computed(() => {
    return [
      {
        color: 'var(--secondary-color)',
        label: translateString('courses.guidedQuizCompletionsChart.previouslyPassedPercentage'),
        show: guidedQuizCompletions.value.length > 1,
      },
      {
        color: 'var(--primary-color)',
        label: translateString('courses.guidedQuizCompletionsChart.latestPassedPercentage'),
        show: true,
      },
    ]
  })

  const hasPassedGuidedQuiz = computed(() => {
    const total = points.correct + points.wrong
    return total > 0 && points.correct / total >= 0.5
  })

  const showNonFreeLesson = computed(
    () => !(isAPurchasedCourse.value || currentModule.value?.isFree) && !props.isEmbedded,
  )

  const nextQuestion = async (answers: QuestionAnswer[]) => {
    if (questionIndex.value === 0) {
      UserModule.setGetStartedChecklistItem({ item: 'testYourQuizKnowledge', value: true })
    }

    if (props.isEmbedded && questionIndex.value === 2) {
      emit('show-guided-quiz-register-modal')
      return
    }

    if (questionAnswered.value) {
      calculatedPoints.value = true
    } else {
      selectedAnswers.value.push(...answers)
    }

    if (calculatedPoints.value) {
      await handleCalculatedPoints()
      return
    }

    const choice = selectedAnswers.value.find((ans) => ans.questionId === currentQuestion.value!.questionId)
    if (!props.isEmbedded) {
      await handleUnansweredQuestion(choice)
    }
    await handleAnswerQuestion(choice)
  }

  const handleCalculatedPoints = async () => {
    if (
      !actualQuestions.value[questionIndex.value + 1] ||
      (redoingWrongAnswers.value && !wrongQuestions.value[questionIndex.value + 1])
    ) {
      completeGuidedQuiz()
      await handleGuidedQuizCompletion()
      fetchAllGuidedQuizCompletions()
    } else {
      questionIndex.value++
      if (!questionAnswered.value) {
        calculatedPoints.value = false
      }
    }
  }

  const completeGuidedQuiz = () => {
    trackIddPoints()
    completedGuidedQuiz.value = true
    trackGamificationActivity('GUIDED_QUIZ_COMPLETED', {
      courseId: currentCourseId.value,
      moduleId: moduleId.value,
      lessonId: null,
    })
  }

  const trackIddPoints = () => {
    if (course.value!.isIddCourse && hasPassedGuidedQuiz.value) {
      UsersApi.trackUserIddPoints(UserModule.auth0Id, course.value!.id)
    }
  }

  const handleGuidedQuizCompletion = async () => {
    if (currentGuidedQuizCompletionId.value) {
      await guidedQuizCompleted(
        guidedQuiz.value!.id!,
        currentGuidedQuizCompletionId.value,
        currentCourseId.value,
        course.value!.title,
        moduleId.value,
        currentModule.value?.name || '',
      )
    }
  }

  const handleUnansweredQuestion = async (choice: QuestionAnswer | undefined) => {
    postingQuizAnswer.value = true

    const resp = await CourseApi.postGuidedQuizAnswer(currentCourseId.value, moduleId.value, {
      completionId: currentGuidedQuizCompletionId.value,
      questionId: currentQuestion.value!.questionId,
      selectedAnswerIds: choice!.selectedAnswerIds,
      numberAnswer: convertNumberAnswer(choice?.numberAnswer || ''),
      state: isLastQuestion.value ? 'completed' : '',
    })

    CourseModule.setLatestGuidedQuizCompletionId({
      courseId: currentCourseId.value,
      moduleId: moduleId.value,
      completionId: resp.completionId,
    })

    updateCurrentQuestionInCompletion(
      currentQuestion.value?.questionId,
      currentQuestion.value?.questionType,
      choice!.selectedAnswerIds,
    )
    postingQuizAnswer.value = false
  }

  const handleAnswerQuestion = async (choice: QuestionAnswer | undefined) => {
    if (currentQuestion.value?.questionType === 'number') {
      const numberAnswer = convertNumberAnswer(choice?.numberAnswer ?? '')
      if (correctNumberAnswer(numberAnswer, currentCorrectAnswers.value[0].answer)) {
        points.correct++
        trackGamificationActivity('GUIDED_QUIZ_QUESTION_ANSWERED_CORRECTLY', {
          courseId: currentCourseId.value,
          moduleId: moduleId.value,
          lessonId: null,
        })
      } else {
        points.wrong++
      }
    } else {
      if (
        currentCorrectAnswers.value?.length === choice?.selectedAnswerIds.length &&
        currentCorrectAnswers.value?.every((answer) => choice?.selectedAnswerIds.includes(answer.answerId))
      ) {
        points.correct++
        trackGamificationActivity('GUIDED_QUIZ_QUESTION_ANSWERED_CORRECTLY', {
          courseId: currentCourseId.value,
          moduleId: moduleId.value,
          lessonId: null,
        })
      } else {
        points.wrong++
      }
    }

    calculatedPoints.value = true
  }

  const previousQuestion = () => {
    calculatedPoints.value = true
    questionIndex.value--
  }

  const abortQuiz = () => {
    startedQuiz.value = false
    resetQuiz()
    CourseModule.getGuidedQuizCompletion({
      courseId: currentCourseId.value,
      moduleId: moduleId.value,
    })
  }

  const redoWrongAnswers = () => {
    questionIndex.value = 0
    completedGuidedQuiz.value = false
    calculatedPoints.value = false
    points.wrong = 0
    redoingWrongAnswers.value = true
    wrongQuestions.value = getWrongQuestions.value || []
    wrongQuestions.value.forEach((q) => {
      const index = selectedAnswers.value.findIndex((ans) => ans.questionId === q.questionId)
      if (index !== -1) {
        selectedAnswers.value.splice(index, 1)
      }
      q.answers.forEach((a) => (a.selected = false))
    })
  }

  const redoWholeTest = async () => {
    fetchingQuizCompletion.value = true
    scrollToTop()
    CourseModule.setGuidedQuizCompletion({ [moduleId.value]: null })

    await CourseModule.getGuidedQuizCompletion({
      courseId: currentCourseId.value,
      moduleId: moduleId.value,
    }).finally(() => {
      if (guidedQuizCompletion.value?.completionId) {
        CourseModule.setLatestGuidedQuizCompletionId({
          courseId: currentCourseId.value,
          moduleId: moduleId.value,
          completionId: guidedQuizCompletion.value.completionId,
        })
      }

      fetchingQuizCompletion.value = false
    })
    resetQuiz()
  }

  const resetQuiz = () => {
    selectedAnswers.value = []
    questionIndex.value = 0
    completedGuidedQuiz.value = false
    calculatedPoints.value = false
    points.wrong = 0
    points.correct = 0
    redoingWrongAnswers.value = false
  }

  const nextLessonAction = () => {
    const params: { [key: string]: string } = {}
    const mod = nextCourseModule()
    const lesson = nextLesson()
    if (mod) params.moduleId = mod.id
    if (lesson) params.lessonId = params.lessonId = lesson.id

    router
      .push({
        name: lesson ? 'Lesson' : 'CourseModule',
        params,
      })
      .catch(() => {
        return
      })
  }

  const updateCurrentQuestionInCompletion = (
    questionId: string,
    questionType: 'single' | 'multiple' | 'number',
    selectedAnsIds: string[],
  ) => {
    selectedAnsIds.forEach((ansId) => {
      if (guidedQuizCompletion.value) {
        const currentQuestionInCompletion =
          guidedQuizCompletion.value.questions[currentQuestionIndexInCompletion.value(questionId)!]
        if (questionType === 'number') {
          currentQuestionInCompletion.userNumberAnswer = ansId
        } else {
          currentQuestionInCompletion?.answers.forEach((ans) => {
            if (ans.answerId === ansId) {
              ans.selected = true
            }
          })
        }
      }
    })
  }

  const fetchAllGuidedQuizCompletions = () => {
    CourseApi.getGuidedQuizCompletions(currentCourseId.value, moduleId.value).then((completions) => {
      guidedQuizCompletions.value = completions
    })
  }

  const fetchSpecificGuidedQuizCompletion = (completionId: string) => {
    CourseApi.getSpecificGuidedQuizCompletion(currentCourseId.value, moduleId.value, completionId)
      .then((data) => {
        if (data[0] && data[0].questions) {
          const { questions } = data[0]
          CourseModule.setGuidedQuizCompletion({ [moduleId.value]: data[0] })
          questions.forEach((question) => {
            let userAnswers = question.answers.filter((ans) => ans.selected)
            if (question.questionType === 'number' && question.userNumberAnswer) {
              userAnswers = [{ answerId: question.answers[0].answerId, answer: question.userNumberAnswer }]
            }
            if (userAnswers.length) {
              selectedAnswers.value.push({
                questionId: question.questionId,
                selectedAnswerIds: userAnswers.map((ans) => ans.answerId),
                numberAnswer: question.userNumberAnswer,
              })
              questionIndex.value++
              const correctAnswers = question.answers.filter((a) => a.isCorrect)
              const isCorrectNumberQuestion =
                question.questionType === 'number'
                  ? correctNumberAnswer(question.userNumberAnswer ?? '', question.answers[0].answer)
                  : true
              if (
                answeredQuestionCorrectly.value(
                  userAnswers.map((a) => a.answerId),
                  correctAnswers.map((a) => a.answerId),
                ) &&
                isCorrectNumberQuestion
              ) {
                points.correct++
              } else points.wrong++
            }
          })
          if (selectedAnswers.value.length) startedQuiz.value = true
          if (selectedAnswers.value.length === questions.length) {
            completedGuidedQuiz.value = true
          }
        }
      })
      .finally(() => {
        fetchingQuizCompletion.value = false
      })
  }

  const getGuidedQuizCompletion = () => {
    if (currentGuidedQuizCompletionId.value) {
      fetchSpecificGuidedQuizCompletion(currentGuidedQuizCompletionId.value)
    } else {
      CourseModule.getGuidedQuizCompletion({
        courseId: currentCourseId.value,
        moduleId: moduleId.value,
      })
        .then(() => {
          if (guidedQuizCompletion.value) {
            CourseModule.setLatestGuidedQuizCompletionId({
              courseId: currentCourseId.value,
              moduleId: moduleId.value,
              completionId: guidedQuizCompletion.value.completionId,
            })
          }
        })
        .finally(() => {
          fetchingQuizCompletion.value = false
          if (fromEmbeddedQuiz.value) {
            startedQuiz.value = true
            CookieHelper.removeCrossDomainCookie('fromEmbeddedQuiz')
          }
        })
    }
  }

  watch(currentQuestion, () => {
    if (!questionAnswered.value && currentQuestion.value) {
      if (currentQuestion.value.answers) {
        if (guidedQuizCompletion.value && !redoingWrongAnswers.value) {
          guidedQuizCompletion.value.questions[
            currentQuestionIndexInCompletion.value(currentQuestion.value.questionId)!
          ].answers = shuffleArray(currentQuestion.value.answers)
        }
        currentQuestion.value.answers = currentQuestion.value.answers.map((a) => ({
          ...a,
          selected: false,
        }))
      }
    }
  })

  const handleGuidedQuizLifecycle = () => {
    if (props.isEmbedded) {
      trackPage('Embedded Guided Quiz')
      pageViewed('Embedded Guided Quiz')
      CourseModule.getEmbeddedGuidedQuizCompletion({
        courseId: currentCourseId.value,
        moduleId: moduleId.value,
      }).finally(() => {
        fetchingEmbeddedQuizCompletion.value = false
        // start the quiz automatically
        startedQuiz.value = true
      })
    } else {
      trackPage('Guided Quiz')
      pageViewed('Guided Quiz')
      getGuidedQuizCompletion()
      fetchAllGuidedQuizCompletions()
    }
  }

  watch(moduleId, () => {
    // user could be moving from one guided quiz to another
    startedQuiz.value = false
    fetchingQuizCompletion.value = true
    resetQuiz()
    handleGuidedQuizLifecycle()
  })

  watch(fetchingEmbeddedQuizCompletion, (value) => {
    if (!value) {
      emit('embedded-quiz-loaded')
    }
  })

  onMounted(() => {
    handleGuidedQuizLifecycle()
  })

return { __sfc: true,props, emit, router, currentCourseId, moduleId, course, studyingStatus, nextCourseModule, nextLesson, trackPage, pageViewed, guidedQuizCompleted, trackGamificationActivity, convertNumberAnswer, shuffleArray, formatDate, getColorVariableValue, correctNumberAnswer, fromEmbeddedQuiz, scrollToTop, translateString, arrowRight, isMobileDevice, isDesktopDevice, fetchingQuizCompletion, fetchingEmbeddedQuizCompletion, questionIndex, wrongQuestions, selectedAnswers, startedQuiz, calculatedPoints, completedGuidedQuiz, postingQuizAnswer, redoingWrongAnswers, guidedQuizCompletions, loadingGuidedQuiz, currentModule, guidedQuiz, currentGuidedQuizCompletionId, actualQuestions, currentQuestion, currentCorrectAnswers, points, questionAnswered, isLastQuestion, getWrongQuestions, totalQuestions, hasNewQuestions, guidedQuizCompletion, latestCompletionQuestions, currentQuestionIndexInCompletion, isAPurchasedCourse, answeredQuestionCorrectly, completionsDateChartLabels, completionsPassedPercentageChartData, chartBackgroundColors, completionsChartData, chartLabels, hasPassedGuidedQuiz, showNonFreeLesson, nextQuestion, handleCalculatedPoints, completeGuidedQuiz, trackIddPoints, handleGuidedQuizCompletion, handleUnansweredQuestion, handleAnswerQuestion, previousQuestion, abortQuiz, redoWrongAnswers, redoWholeTest, resetQuiz, nextLessonAction, updateCurrentQuestionInCompletion, fetchAllGuidedQuizCompletions, fetchSpecificGuidedQuizCompletion, getGuidedQuizCompletion, handleGuidedQuizLifecycle, computed, onMounted, reactive, ref, watch, KetchUpButton, LoadingSpinner, SVGRenderer, useCourse, CourseModule, useIcons, NonFreeLesson, useCommonMixin, CourseApi, QuestionWithButtons, useSegment, useBreakpoint, useGamification, GuidedQuizResults, BarChart, useRouter, useI18n, UsersApi, UserModule, CookieHelper }
}

})